import { useState } from "react"
import { Check, Copy, Download, FileCode2, GitBranch } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PromptDetail, PromptSummary } from "@/lib/prompt-audit-api"
import type { ReaderMode } from "./prompt-reader"

const KIND_LABEL: Record<string, string> = {
  surface: "System prompt",
  fragment: "Fragment",
  document: "Document",
  tools: "Tool palette",
  template: "Template",
}

function CopyValue({ label, value, display }: { label: string; value: string; display?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }, () => toast.error("Copy failed"))
      }}
      className="inline-flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <span className="truncate">{display ?? value}</span>
      {copied ? <Check className="size-3 shrink-0 text-emerald-600" aria-hidden="true" /> : <Copy className="size-3 shrink-0" aria-hidden="true" />}
    </button>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="text-sm font-medium tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

function Chips({ label, ids, titles, onPick }: { label: string; ids: string[]; titles: Record<string, string>; onPick: (id: string) => void }) {
  if (!ids.length) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs text-muted-foreground">{label}</span>
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onPick(id)}
          className="rounded-full border px-2 py-0.5 text-xs text-foreground/80 hover:bg-muted hover:text-foreground"
        >
          {titles[id] ?? id}
        </button>
      ))}
    </div>
  )
}

type HeaderProps = {
  detail: PromptDetail
  summaries: Record<string, PromptSummary>
  mode: ReaderMode
  showResolved: boolean
  canComment: boolean
  commentsAvailable: boolean
  onMode: (mode: ReaderMode) => void
  onShowResolved: (show: boolean) => void
  onNavigate: (id: string) => void
  onJump: (offset: number) => void
  exportReady: boolean
  onExport: () => void
}

export function PromptHeader({
  detail, summaries, mode, showResolved, canComment, commentsAvailable,
  onMode, onShowResolved, onNavigate, onJump, exportReady, onExport,
}: HeaderProps) {
  const titles = Object.fromEntries(Object.values(summaries).map((entry) => [entry.id, entry.title]))
  const tokens = detail.tokens ?? detail.approx_tokens
  return (
    <header className="space-y-5">
      <div>
        <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {detail.group} · {KIND_LABEL[detail.kind] ?? detail.kind}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-balance">{detail.title}</h1>
        {detail.description && <p className="mt-2 max-w-[68ch] text-sm text-pretty text-muted-foreground">{detail.description}</p>}
      </div>

      {detail.error && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          This prompt could not be assembled in this process ({detail.error}).
        </div>
      )}

      {detail.live_state === "absent" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
          <p className="font-semibold text-amber-800 dark:text-amber-200">No live copy in the configuration bucket</p>
          <p className="mt-1 text-foreground/80">
            {detail.live_source} The repo copy has {detail.repo_chars?.toLocaleString()} characters,
            hash <span className="font-mono text-xs">{detail.repo_sha256?.slice(0, 12)}</span>, and is not what the model reads.
          </p>
        </div>
      )}

      {detail.live_state === "not_loaded" && (
        <div className="rounded-lg border bg-muted/40 p-4 text-sm text-foreground/80">
          This process has not read the bucket copy yet, so the repo file is shown. Reload once the server has refreshed its configuration.
        </div>
      )}

      {detail.drift && detail.live_state !== "absent" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
          <p className="font-semibold text-amber-800 dark:text-amber-200">The live copy differs from the repo copy</p>
          <p className="mt-1 text-foreground/80">
            Shown: the live copy ({detail.chars.toLocaleString()} characters). Repo copy: {detail.repo_chars?.toLocaleString()} characters,
            hash <span className="font-mono text-xs">{detail.repo_sha256?.slice(0, 12)}</span>.
          </p>
        </div>
      )}

      <dl className="flex flex-wrap gap-x-8 gap-y-3">
        <Stat label="Characters" value={detail.chars.toLocaleString()} />
        <Stat label={detail.tokens === null ? "Tokens (approx.)" : "Tokens"} value={tokens.toLocaleString()} />
        {detail.tool_count !== null && <Stat label="Tools" value={String(detail.tool_count)} />}
      </dl>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          <FileCode2 className="size-3.5 text-muted-foreground" aria-hidden="true" />
          <CopyValue label="source" value={detail.source} />
          {detail.live_source && detail.live_state !== "absent" && <span className="text-xs text-muted-foreground">({detail.live_source})</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <GitBranch className="size-3.5 text-muted-foreground" aria-hidden="true" />
          <CopyValue label="content hash" value={detail.sha256} display={`sha256 ${detail.sha256.slice(0, 16)}`} />
          {detail.wire_sha256 && <CopyValue label="wire hash" value={detail.wire_sha256} display={`wire ${detail.wire_sha256.slice(0, 16)}`} />}
        </div>
      </div>

      <div className="space-y-2">
        <Chips label="Included in" ids={detail.included_in} titles={titles} onPick={onNavigate} />
        <Chips label="Templated into" ids={detail.templated_in} titles={titles} onPick={onNavigate} />
        {detail.contains.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              Built from {detail.contains.length} named {detail.contains.length === 1 ? "piece" : "pieces"}
            </summary>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {detail.contains.map((part) => (
                <button
                  key={`${part.source}-${part.start}`}
                  type="button"
                  onClick={() => onJump(part.start)}
                  className="rounded-full border px-2 py-0.5 text-xs text-foreground/80 hover:bg-muted hover:text-foreground"
                >
                  {part.label}
                </button>
              ))}
            </div>
          </details>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-y py-2">
        <div role="radiogroup" aria-label="View" className="inline-flex rounded-lg border p-0.5">
          {(["rendered", "source"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              onClick={() => onMode(value)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium capitalize",
                mode === value ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {value}
            </button>
          ))}
        </div>
        <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={showResolved} onChange={(event) => onShowResolved(event.target.checked)} className="accent-primary" />
          Show resolved
        </label>
        <span className="ml-auto hidden text-xs text-muted-foreground md:inline">
          {!commentsAvailable
            ? "Comments are not set up on this environment yet."
            : canComment ? "Select text to comment." : "Turn off read-only to comment."}
        </span>
        <CopyText text={detail.text} />
        <Button size="sm" variant="outline" onClick={onExport} aria-disabled={!exportReady}>
          <Download />
          Export with comments
        </Button>
      </div>
    </header>
  )
}

function CopyText({ text }: { text: string }) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Prompt copied"), () => toast.error("Copy failed"))}
    >
      <Copy />
      Copy text
    </Button>
  )
}
